<template>
  <div class="suga-remote">
    <v-btn
      v-if="showButton"
      color="primary"
      variant="elevated"
      @click="openDialog"
      prepend-icon="mdi-cash-multiple"
    >
      수가 관리
    </v-btn>

    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title class="dialog-title">
          수가 관리
          <v-spacer></v-spacer>
          <v-btn icon variant="text" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <!-- 연도 선택 영역 -->
          <div class="year-selector">
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              label="연도 선택"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="loadSuga"
              class="year-select"
              :disabled="loading"
            />
            <v-btn
              color="primary"
              variant="outlined"
              @click="addSuga"
              prepend-icon="mdi-plus"
              class="add-btn"
              :disabled="loading"
            >
              추가
            </v-btn>
          </div>

          <!-- Fallback 알림 -->
          <v-alert
            v-if="isFallback && actualYear"
            type="info"
            variant="tonal"
            density="compact"
            class="fallback-alert"
          >
            {{ selectedYear }}년 수가 데이터가 없어 {{ actualYear }}년 데이터를 불러왔습니다.
            <strong>저장 시 {{ selectedYear }}년 데이터로 저장됩니다.</strong>
          </v-alert>

          <!-- 로딩 상태 -->
          <div v-if="loading" class="loading-container">
            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
            <span>수가 데이터를 불러오는 중...</span>
          </div>

          <!-- 수가 테이블 -->
          <div v-else class="suga-table-container compact">
            <table class="suga-table">
              <thead>
                <tr>
                  <th>시간</th>
                  <th>수가</th>
                  <th>본인부담금</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="suga in sugaList"
                  :key="suga.suga_idx || suga.tempId"
                  :data-suga-id="suga.tempId"
                  :class="{ 'is-deleted': suga.isDeleted, 'is-new': suga.isNew }"
                >
                  <td>
                    <v-text-field
                      v-model="suga.suga_rank"
                      variant="outlined"
                      density="compact"
                      hide-details
                      placeholder="시간 (예: 60분)"
                      class="rank-input"
                      :disabled="suga.isDeleted"
                      @input="validateSuga(suga)"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model="suga.suga_money"
                      type="text"
                      variant="outlined"
                      density="compact"
                      hide-details
                      placeholder="수가"
                      class="money-input"
                      :disabled="suga.isDeleted"
                      @focus="onMoneyFocus($event, suga)"
                      @blur="onMoneyBlur($event, suga)"
                      @input="onMoneyInput($event, suga)"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model="suga.suga_bonin"
                      type="text"
                      variant="outlined"
                      density="compact"
                      hide-details
                      placeholder="본인부담금"
                      class="bonin-input"
                      :disabled="suga.isDeleted"
                      @focus="onBoninFocus($event, suga)"
                      @blur="onBoninBlur($event, suga)"
                      @input="onBoninInput($event, suga)"
                    />
                  </td>
                  <td>
                    <div class="action-buttons">
                      <v-btn
                        v-if="!suga.isDeleted"
                        color="error"
                        variant="outlined"
                        @click="removeSuga(suga)"
                        size="small"
                        icon
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                      <v-btn
                        v-else
                        color="success"
                        variant="outlined"
                        @click="restoreSuga(suga)"
                        size="small"
                        icon
                      >
                        <v-icon>mdi-undo</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>
                <tr v-if="sugaList.length === 0" class="no-suga">
                  <td colspan="4" class="text-center">
                    <v-icon color="grey">mdi-cash-off</v-icon>
                    <span>수가 데이터가 없습니다</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 수가 요약 정보 -->
          <div v-if="sugaList.length > 0 && !loading" class="suga-summary">
            <span>총 {{ activeSugaCount }}개 수가</span>
            <span v-if="deletedCount > 0" class="deleted-count">(삭제 예정: {{ deletedCount }}개)</span>
            <span v-if="newCount > 0" class="new-count">(신규: {{ newCount }}개)</span>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dialog = false" :disabled="saving">취소</v-btn>
          <v-btn 
            color="primary" 
            variant="elevated" 
            @click="saveSuga" 
            :loading="saving"
            :disabled="loading || !hasChanges"
          >
            <v-icon class="mr-1">mdi-content-save</v-icon>
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">닫기</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue';
import axios from 'axios';

// Props
interface Props {
  showButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showButton: false
});

// Emits
const emit = defineEmits<{
  (e: 'saved'): void;
  (e: 'updated', year: number): void;
}>();

interface Suga {
  suga_idx?: number;
  suga_biz_gubun: string;
  suga_year: string;
  suga_rank: string;
  suga_money: number | string;
  suga_bonin: number | string;
  isNew?: boolean;
  isDeleted?: boolean;
  isModified?: boolean;
  tempId?: string;
  isValid?: boolean;
  originalData?: {
    suga_rank: string;
    suga_money: number | string;
    suga_bonin: number | string;
  };
}

const dialog = ref(false);
const selectedYear = ref(new Date().getFullYear());
const sugaList = ref<Suga[]>([]);
const originalSugaList = ref<Suga[]>([]);
const loading = ref(false);
const saving = ref(false);
const isFallback = ref(false);
const actualYear = ref<string | null>(null);
const snackbar = ref({ 
  show: false, 
  message: '', 
  color: 'success',
  icon: 'mdi-check-circle'
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
});

// 활성 수가 개수 (삭제 예정 제외)
const activeSugaCount = computed(() => {
  return sugaList.value.filter(s => !s.isDeleted).length;
});

// 삭제 예정 개수
const deletedCount = computed(() => {
  return sugaList.value.filter(s => s.isDeleted && !s.isNew).length;
});

// 신규 개수
const newCount = computed(() => {
  return sugaList.value.filter(s => s.isNew && !s.isDeleted).length;
});

// 변경사항 있는지 확인
const hasChanges = computed(() => {
  // 신규 추가된 항목이 있는지
  const hasNew = sugaList.value.some(s => s.isNew && !s.isDeleted);
  
  // 삭제 예정 항목이 있는지
  const hasDeleted = sugaList.value.some(s => s.isDeleted && !s.isNew);
  
  // 수정된 항목이 있는지
  const hasModified = sugaList.value.some(s => {
    if (s.isNew || s.isDeleted) return false;
    if (!s.originalData) return false;
    
    const currentMoney = parseMoneyValue(s.suga_money);
    const originalMoney = parseMoneyValue(s.originalData.suga_money);
    const currentBonin = parseMoneyValue(s.suga_bonin);
    const originalBonin = parseMoneyValue(s.originalData.suga_bonin);
    
    return s.suga_rank !== s.originalData.suga_rank ||
           currentMoney !== originalMoney ||
           currentBonin !== originalBonin;
  });
  
  return hasNew || hasDeleted || hasModified;
});

const loadSuga = async () => {
  loading.value = true;
  isFallback.value = false;
  actualYear.value = null;
  
  try {
    const response = await axios.get(`/api/calc/suga/${selectedYear.value}`);
    if (response.data.success) {
      const data = response.data.data || [];
      
      // Fallback 정보 처리
      if (response.data.isFallback) {
        isFallback.value = true;
        actualYear.value = response.data.year;
      }
      
      sugaList.value = data.map((s: Suga) => ({
        ...s,
        suga_money: formatMoney(s.suga_money || 0),
        suga_bonin: formatMoney(s.suga_bonin || 0),
        isNew: false,
        isDeleted: false,
        isModified: false,
        isValid: true,
        originalData: {
          suga_rank: s.suga_rank,
          suga_money: s.suga_money,
          suga_bonin: s.suga_bonin
        }
      }));
      
      // 원본 데이터 저장
      originalSugaList.value = JSON.parse(JSON.stringify(sugaList.value));
    } else {
      sugaList.value = [];
    }
  } catch (error: any) {
    console.error('수가 조회 실패:', error);
    
    if (error.response?.status === 404) {
      // 데이터가 없는 경우
      sugaList.value = [];
      showSnackbar(`${selectedYear.value}년 수가 데이터가 없습니다. 새로 추가해주세요.`, 'info', 'mdi-information');
    } else {
      showSnackbar('수가 조회에 실패했습니다', 'error', 'mdi-alert-circle');
    }
  } finally {
    loading.value = false;
  }
};

const addSuga = () => {
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const newSuga: Suga = {
    suga_biz_gubun: '방문요양',
    suga_year: selectedYear.value.toString(),
    suga_rank: '',
    suga_money: '',
    suga_bonin: '',
    isNew: true,
    isDeleted: false,
    isModified: false,
    tempId,
    isValid: false
  };
  
  sugaList.value.push(newSuga);
  
  // 새로 추가된 항목에 포커스
  nextTick(() => {
    const rankInput = document.querySelector(`[data-suga-id="${tempId}"] .rank-input input`) as HTMLInputElement;
    if (rankInput) {
      rankInput.focus();
    }
  });
};

const validateSuga = (suga: Suga) => {
  const rank = suga.suga_rank?.trim();
  suga.isValid = Boolean(rank && rank.length > 0);
  
  if (rank && rank.length > 50) {
    suga.suga_rank = rank.substring(0, 50);
    showSnackbar('시간은 50자 이내로 입력해주세요', 'warning', 'mdi-alert');
  }
};

// 금액 포맷팅 함수
const formatMoney = (amount: number | string): string => {
  if (!amount && amount !== 0) return '';
  const numericValue = typeof amount === 'string' ? parseFloat(amount.replace(/[^\d]/g, '')) : amount;
  if (isNaN(numericValue)) return '';
  return new Intl.NumberFormat('ko-KR').format(numericValue);
};

// 금액 입력 필드 포커스 이벤트 (컴마 제거)
const onMoneyFocus = (event: FocusEvent, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  suga.suga_money = numericValue;
};

// 금액 입력 필드 포커스 아웃 이벤트 (컴마 추가)
const onMoneyBlur = (event: FocusEvent, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = formatMoney(parseInt(numericValue));
    input.value = formattedValue;
    suga.suga_money = formattedValue;
  } else {
    input.value = '';
    suga.suga_money = '';
  }
};

// 금액 입력 필드 입력 이벤트 (숫자만 허용)
const onMoneyInput = (event: Event, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  
  if (value.length > 12) {
    value = value.slice(0, 12);
  }
  
  input.value = value;
  suga.suga_money = value;
};

// 본인부담금 입력 필드 포커스 이벤트 (컴마 제거)
const onBoninFocus = (event: FocusEvent, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  suga.suga_bonin = numericValue;
};

// 본인부담금 입력 필드 포커스 아웃 이벤트 (컴마 추가)
const onBoninBlur = (event: FocusEvent, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = formatMoney(parseInt(numericValue));
    input.value = formattedValue;
    suga.suga_bonin = formattedValue;
  } else {
    input.value = '';
    suga.suga_bonin = '';
  }
};

// 본인부담금 입력 필드 입력 이벤트 (숫자만 허용)
const onBoninInput = (event: Event, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  
  if (value.length > 12) {
    value = value.slice(0, 12);
  }
  
  input.value = value;
  suga.suga_bonin = value;
};

const removeSuga = (suga: Suga) => {
  if (suga.isNew) {
    // 신규 추가된 항목은 바로 삭제
    const index = sugaList.value.indexOf(suga);
    sugaList.value.splice(index, 1);
    showSnackbar('수가를 삭제했습니다', 'info', 'mdi-delete');
  } else {
    // 기존 항목은 삭제 표시
    suga.isDeleted = true;
    showSnackbar('저장 시 삭제됩니다. 취소하려면 복원 버튼을 클릭하세요.', 'warning', 'mdi-delete-outline');
  }
};

// 삭제 취소 (복원)
const restoreSuga = (suga: Suga) => {
  suga.isDeleted = false;
  showSnackbar('삭제가 취소되었습니다', 'success', 'mdi-undo');
};

// 숫자 문자열을 숫자로 변환하는 함수
const parseMoneyValue = (value: number | string): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  return parseFloat(String(value).replace(/[^\d]/g, '')) || 0;
};

const saveSuga = async () => {
  // 유효성 검사
  const invalidSuga = sugaList.value.filter(s => 
    !s.isDeleted && !s.suga_rank?.trim()
  );
  
  if (invalidSuga.length > 0) {
    showSnackbar('시간을 입력해주세요', 'error', 'mdi-alert-circle');
    return;
  }

  saving.value = true;
  try {
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    // 1. 삭제 처리
    const deletes = sugaList.value.filter(s => s.isDeleted && s.suga_idx);
    for (const s of deletes) {
      try {
        await axios.delete(`/api/calc/suga/${s.suga_idx}`);
        successCount++;
      } catch (error) {
        errorCount++;
        errors.push(`삭제 실패: ${s.suga_rank}`);
      }
    }

    // 2. 추가 처리
    const inserts = sugaList.value.filter(s => s.isNew && !s.isDeleted && s.suga_rank.trim());
    for (const s of inserts) {
      try {
        await axios.post('/api/calc/suga', {
          suga_biz_gubun: s.suga_biz_gubun || '방문요양',
          suga_year: selectedYear.value.toString(),
          suga_rank: s.suga_rank.trim(),
          suga_money: parseMoneyValue(s.suga_money),
          suga_bonin: parseMoneyValue(s.suga_bonin)
        });
        successCount++;
      } catch (error) {
        errorCount++;
        errors.push(`추가 실패: ${s.suga_rank}`);
      }
    }

    // 3. 수정 처리
    const updates = sugaList.value.filter(s => {
      if (s.isNew || s.isDeleted || !s.suga_idx) return false;
      if (!s.originalData) return false;
      
      const currentMoney = parseMoneyValue(s.suga_money);
      const originalMoney = parseMoneyValue(s.originalData.suga_money);
      const currentBonin = parseMoneyValue(s.suga_bonin);
      const originalBonin = parseMoneyValue(s.originalData.suga_bonin);
      
      return s.suga_rank !== s.originalData.suga_rank ||
             currentMoney !== originalMoney ||
             currentBonin !== originalBonin;
    });
    
    for (const s of updates) {
      try {
        await axios.put(`/api/calc/suga/${s.suga_idx}`, {
          suga_biz_gubun: s.suga_biz_gubun || '방문요양',
          suga_year: selectedYear.value.toString(),
          suga_rank: s.suga_rank.trim(),
          suga_money: parseMoneyValue(s.suga_money),
          suga_bonin: parseMoneyValue(s.suga_bonin)
        });
        successCount++;
      } catch (error) {
        errorCount++;
        errors.push(`수정 실패: ${s.suga_rank}`);
      }
    }

    if (errorCount > 0) {
      showSnackbar(`일부 저장 실패: ${errorCount}건 (성공: ${successCount}건)`, 'warning', 'mdi-alert');
      console.error('저장 오류:', errors);
    } else if (successCount > 0) {
      showSnackbar(`저장 완료! (${successCount}건 처리)`, 'success', 'mdi-check-circle');
      emit('saved');
      emit('updated', selectedYear.value);
    } else {
      showSnackbar('변경사항이 없습니다', 'info', 'mdi-information');
    }
    
    // 데이터 새로고침
    await loadSuga();
  } catch (error) {
    console.error('저장 실패:', error);
    showSnackbar('저장 중 오류가 발생했습니다', 'error', 'mdi-alert-circle');
  } finally {
    saving.value = false;
  }
};

const showSnackbar = (message: string, color: string = 'success', icon: string = 'mdi-check-circle') => {
  snackbar.value = { show: true, message, color, icon };
};

// 다이얼로그 열릴 때 데이터 로드
const openDialog = () => {
  dialog.value = true;
  loadSuga();
};

// 다이얼로그가 닫힐 때 상태 초기화
watch(dialog, (newValue) => {
  if (!newValue) {
    // 다이얼로그 닫힐 때 상태 초기화
    isFallback.value = false;
    actualYear.value = null;
  }
});

// 외부에서 접근 가능하도록 노출
defineExpose({
  openDialog,
  loadSuga
});
</script>

<style lang="scss" scoped>
@import './SugaRemote.scss';
</style>
